/**
 * Enhanced Console Management System
 * Provides runtime control over console logging with environment-based defaults
 * and developer-friendly window controls
 */

export type LogLevel = 'NONE' | 'MINIMAL' | 'DETAILED';

interface ConsoleState {
  level: LogLevel;
  originalMethods: {
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
    debug: typeof console.debug;
    trace: typeof console.trace;
    group: typeof console.group;
    groupCollapsed: typeof console.groupCollapsed;
    groupEnd: typeof console.groupEnd;
    clear: typeof console.clear;
    count: typeof console.count;
    countReset: typeof console.countReset;
    time: typeof console.time;
    timeEnd: typeof console.timeEnd;
    timeLog: typeof console.timeLog;
    table: typeof console.table;
    dir: typeof console.dir;
    dirxml: typeof console.dirxml;
    assert: typeof console.assert;
  };
  isIntercepted: boolean;
  stats: {
    totalLogs: number;
    filteredLogs: number;
    lastLog: string | null;
  };
}

class ConsoleManager {
  private state: ConsoleState;

  constructor() {
    // Store original console methods
    this.state = {
      level: this.getInitialLogLevel(),
      originalMethods: {
        log: console.log.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        debug: console.debug.bind(console),
        trace: console.trace.bind(console),
        group: console.group.bind(console),
        groupCollapsed: console.groupCollapsed.bind(console),
        groupEnd: console.groupEnd.bind(console),
        clear: console.clear.bind(console),
        count: console.count.bind(console),
        countReset: console.countReset.bind(console),
        time: console.time.bind(console),
        timeEnd: console.timeEnd.bind(console),
        timeLog: console.timeLog.bind(console),
        table: console.table.bind(console),
        dir: console.dir.bind(console),
        dirxml: console.dirxml.bind(console),
        assert: console.assert.bind(console)
      },
      isIntercepted: false,
      stats: {
        totalLogs: 0,
        filteredLogs: 0,
        lastLog: null
      }
    };

    this.initialize();
    this.setupWindowControls();
  }

  private getInitialLogLevel(): LogLevel {
    const envLevel = import.meta.env.VITE_LOG_LEVEL?.toUpperCase();
    
    // Default to DETAILED in development, MINIMAL in production
    if (envLevel === 'NONE' || envLevel === 'MINIMAL' || envLevel === 'DETAILED') {
      return envLevel as LogLevel;
    }
    
    return import.meta.env.DEV ? 'DETAILED' : 'MINIMAL';
  }

  private initialize() {
    this.interceptConsole();
    
    if (import.meta.env.DEV) {
      this.state.originalMethods.log(
        `🎛️ Console Manager initialized - Level: ${this.state.level}`
      );
      this.state.originalMethods.log(
        '💡 Use window.setLogLevel("DETAILED"|"MINIMAL"|"NONE") to control logs'
      );
    }
  }

  private interceptConsole() {
    if (this.state.isIntercepted) return;

    const createInterceptor = (
      methodName: keyof typeof this.state.originalMethods,
      level: 'debug' | 'info' | 'warn' | 'error'
    ) => {
      return (...args: any[]) => {
        this.state.stats.totalLogs++;
        this.state.stats.lastLog = new Date().toISOString();

        if (this.shouldLog(level)) {
          (this.state.originalMethods[methodName] as any)(...args);
        } else {
          this.state.stats.filteredLogs++;
        }
      };
    };

    // Intercept main logging methods
    console.log = createInterceptor('log', 'info');
    console.info = createInterceptor('info', 'info');
    console.debug = createInterceptor('debug', 'debug');
    console.warn = createInterceptor('warn', 'warn');
    console.error = createInterceptor('error', 'error');

    // Keep utility methods working but track them
    const utilityMethods = [
      'trace', 'group', 'groupCollapsed', 'groupEnd', 
      'clear', 'count', 'countReset', 'time', 'timeEnd', 
      'timeLog', 'table', 'dir', 'dirxml', 'assert'
    ] as const;

    utilityMethods.forEach(method => {
      (console as any)[method] = (...args: any[]) => {
        this.state.stats.totalLogs++;
        if (this.shouldLog('info')) {
          (this.state.originalMethods[method] as any)(...args);
        } else {
          this.state.stats.filteredLogs++;
        }
      };
    });

    this.state.isIntercepted = true;
  }

  private shouldLog(level: 'debug' | 'info' | 'warn' | 'error'): boolean {
    switch (this.state.level) {
      case 'NONE':
        return false;
      case 'MINIMAL':
        return level === 'warn' || level === 'error';
      case 'DETAILED':
        return true;
      default:
        return true;
    }
  }

  private setupWindowControls() {
    if (typeof window !== 'undefined') {
      // Developer controls
      (window as any).setLogLevel = (level: LogLevel) => {
        this.setLevel(level);
      };

      (window as any).enableLogs = () => {
        this.setLevel('DETAILED');
      };

      (window as any).disableLogs = () => {
        this.setLevel('NONE');
      };

      (window as any).getLogLevel = () => {
        return this.state.level;
      };

      (window as any).getLogStats = () => {
        return { ...this.state.stats };
      };

      (window as any).restoreConsole = () => {
        this.restore();
      };

      // Add to global for easy access
      (window as any).consoleManager = this;
    }
  }

  public setLevel(level: LogLevel): void {
    const oldLevel = this.state.level;
    this.state.level = level;
    
    if (import.meta.env.DEV || level === 'DETAILED') {
      this.state.originalMethods.log(
        `🎛️ Console level changed: ${oldLevel} → ${level}`
      );
    }
  }

  public getLevel(): LogLevel {
    return this.state.level;
  }

  public restore(): void {
    Object.entries(this.state.originalMethods).forEach(([method, originalFn]) => {
      (console as any)[method] = originalFn;
    });
    
    this.state.isIntercepted = false;
    console.log('🔄 Console methods restored to original state');
  }

  public getStats() {
    return { ...this.state.stats };
  }

  public reset(): void {
    this.state.stats = {
      totalLogs: 0,
      filteredLogs: 0,
      lastLog: null
    };
  }
}

// Create and export singleton instance
export const consoleManager = new ConsoleManager();

// Export convenience functions for compatibility
export const silenceConsole = () => {
  consoleManager.setLevel('NONE');
};

export const enableConsole = () => {
  consoleManager.setLevel('DETAILED');
};

export const setConsoleLevel = (level: LogLevel) => {
  consoleManager.setLevel(level);
};


